import { useState } from 'react'
import type { ApiWebsiteScoreResult } from '../utils/api'

// ── §62: Full 9-metric Website Score Panel ────────────────────────────────────

const METRIC_LABELS: Record<string, string> = {
  visual_design: 'Дизайн',
  ux: 'UX',
  responsive: 'Адаптивность',
  accessibility: 'Доступность',
  seo: 'SEO',
  performance: 'Производительность',
  consistency: 'Консистентность',
  functionality: 'Функциональность',
  security: 'Безопасность',
}

const METRIC_ICONS: Record<string, string> = {
  visual_design: '🎨',
  ux: '🧭',
  responsive: '📱',
  accessibility: '♿',
  seo: '🔍',
  performance: '⚡',
  consistency: '🔄',
  functionality: '⚙️',
  security: '🔐',
}

const SEV_COLOR: Record<string, string> = {
  critical: '#ef4444',
  high: '#f59e0b',
  medium: '#3b82f6',
  low: '#6b7280',
}

const GRADE_COLOR: Record<string, string> = {
  A: '#22c55e', B: '#84cc16', C: '#f59e0b', D: '#f97316', F: '#ef4444',
}

function scoreColor(v: number) {
  return v >= 80 ? '#22c55e' : v >= 60 ? '#f59e0b' : '#ef4444'
}

function ScoreRing({ score, grade, size = 80 }: { score: number; grade: string; size?: number }) {
  const r = (size / 2) - 6
  const c = 2 * Math.PI * r
  const dash = c * (1 - score / 100)
  const color = scoreColor(score)
  return (
    <svg width={size} height={size}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={6} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={6}
        strokeDasharray={c} strokeDashoffset={dash} strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`} style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
      <text x={size / 2} y={size / 2 - 4} textAnchor="middle" fontSize={size / 5} fontWeight={800} fill={color}>{score}</text>
      <text x={size / 2} y={size / 2 + size / 6} textAnchor="middle" fontSize={size / 7} fontWeight={700} fill={GRADE_COLOR[grade] ?? '#6b7280'}>{grade}</text>
    </svg>
  )
}

function MetricBar({ label, icon, value }: { label: string; icon: string; value: number }) {
  const c = scoreColor(value)
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
      <span style={{ fontSize: 14 }}>{icon}</span>
      <div style={{ width: 110, fontSize: 12, color: '#6b7280', flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, height: 6, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${value}%`, background: c, borderRadius: 3, transition: 'width 0.5s ease' }} />
      </div>
      <div style={{ width: 30, fontSize: 12, fontWeight: 700, color: c, textAlign: 'right', flexShrink: 0 }}>{value}</div>
    </div>
  )
}

interface Props {
  result: ApiWebsiteScoreResult
  onClose?: () => void
  compact?: boolean
}

export function WebsiteScorePanel({ result, onClose, compact = false }: Props) {
  const [activeTab, setActiveTab] = useState<'overview' | 'issues'>('overview')
  const criticalIssues = result.issues.filter(i => i.severity === 'critical')
  const highIssues = result.issues.filter(i => i.severity === 'high')
  const otherIssues = result.issues.filter(i => i.severity !== 'critical' && i.severity !== 'high')

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif', color: '#111' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <ScoreRing score={result.overallScore} grade={result.grade} size={compact ? 64 : 80} />
          <div>
            <div style={{ fontSize: compact ? 15 : 18, fontWeight: 700, marginBottom: 4 }}>
              AI Website Score
            </div>
            <div style={{ fontSize: 13, color: '#6b7280', maxWidth: 340, lineHeight: 1.5 }}>
              {result.verdict}
            </div>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9ca3af' }}>×</button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid #e5e7eb', paddingBottom: 8 }}>
        {(['overview', 'issues'] as const).map(t => (
          <button key={t} onClick={() => setActiveTab(t)} style={{
            padding: '5px 14px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: activeTab === t ? 700 : 400,
            background: activeTab === t ? '#111' : 'transparent', color: activeTab === t ? '#fff' : '#6b7280',
          }}>
            {t === 'overview' ? 'Метрики' : `Проблемы (${result.issues.length})`}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div>
          {/* All 9 metrics */}
          {(Object.entries(result.metrics) as Array<[string, number]>).map(([k, v]) => (
            <MetricBar key={k} label={METRIC_LABELS[k] ?? k} icon={METRIC_ICONS[k] ?? '●'} value={v} />
          ))}

          {/* Top priorities */}
          {result.topPriorities.length > 0 && (
            <div style={{ marginTop: 16, padding: 12, background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                Топ приоритеты
              </div>
              {result.topPriorities.map((p, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 4, fontSize: 12 }}>
                  <span style={{ color: '#5b6af0', fontWeight: 700 }}>{i + 1}.</span>
                  <span style={{ color: '#374151' }}>{p}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'issues' && (
        <div>
          {result.issues.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: '#22c55e', fontSize: 13 }}>
              ✓ Проблем не обнаружено
            </div>
          ) : (
            <div>
              {[
                { label: 'Критические', items: criticalIssues, color: '#ef4444', bg: '#fef2f2' },
                { label: 'Высокий приоритет', items: highIssues, color: '#f59e0b', bg: '#fffbeb' },
                { label: 'Прочие', items: otherIssues, color: '#6b7280', bg: '#f9fafb' },
              ].map(({ label, items, color, bg }) => items.length > 0 && (
                <div key={label} style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                    {label} ({items.length})
                  </div>
                  {items.map((issue, i) => (
                    <div key={i} style={{ background: bg, border: `1px solid ${color}33`, borderRadius: 8, padding: 10, marginBottom: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color, textTransform: 'uppercase', background: color + '22', padding: '2px 6px', borderRadius: 4 }}>
                          {issue.category}
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>{issue.title}</span>
                      </div>
                      <div style={{ fontSize: 12, color: '#4b5563', marginBottom: 4 }}>{issue.description}</div>
                      <div style={{ fontSize: 11, color: SEV_COLOR[issue.severity] ?? '#6b7280', display: 'flex', gap: 4 }}>
                        <span style={{ fontWeight: 600 }}>→ Решение:</span>
                        <span>{issue.fix}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
